<template>
  <div class="anamnesis-container">
    <Breadcrumbs />

    <div v-if="isLoading" class="text-center text-gray-600 mt-4">
      Chargement en cours...
    </div>

    <div v-else class="mb-8">
      <!-- Toolbar modernisée avec icônes -->
      <div class="anamnesis-detail-toolbar">
        <div class="anamnesis-detail-toolbar-content">
          <!-- Section titre avec icône et gestion des permissions -->
          <div class="anamnesis-detail-title-section">
            <!-- Icône de localisation -->
            <div class="flex items-center mr-3">
              <MapPin class="h-6 w-6 text-blue-600" />
            </div>

            <div class="relative flex-1">
              <!-- Édition du titre -->
              <input v-if="isEditingTitle && canEdit" ref="titleInput" v-model="editedTitle" type="text"
                placeholder="Nom du lieu" class="anamnesis-detail-title-edit" @blur="handleTitleUpdate"
                @keydown.enter="handleTitleUpdate" @keydown.escape="cancelTitleEdit" />

              <!-- Affichage normal -->
              <h1 v-else class="anamnesis-detail-title" :class="{ readonly: !canEdit }"
                @click="canEdit && startTitleEdit()">
                {{ location?.site || 'Sans nom' }}
              </h1>
            </div>
          </div>

          <!-- Actions avec gestion des permissions -->
          <div class="anamnesis-detail-actions">
            <!-- Toggle public/privé -->
            <button class="anamnesis-detail-status" :class="[
              location?.is_public ? 'public' : 'private',
              { disabled: !canEdit || location?.is_confidential }
            ]" :disabled="!canEdit || location?.is_confidential" @click="canEdit && !location?.is_confidential && handlePublicToggle()">
              <EyeIcon v-if="location?.is_public" class="h-4 w-4" />
              <EyeOffIcon v-else class="h-4 w-4" />
              {{ location?.is_confidential ? 'Privé (confidentiel)' : (location?.is_public ? 'Public' : 'Privé') }}
            </button>

            <!-- Toggle confidentiel -->
            <button class="anamnesis-detail-status" :class="[
              location?.is_confidential ? 'confidential' : 'not-confidential',
              { disabled: !canEdit }
            ]" :disabled="!canEdit" @click="canEdit && handleConfidentialToggle()">
              <LockIcon v-if="location?.is_confidential" class="h-4 w-4" />
              <UnlockIcon v-else class="h-4 w-4" />
              {{ location?.is_confidential ? 'Confidentiel' : 'Non confidentiel' }}
            </button>

            <!-- Boutons d'action - seulement visibles pour les propriétaires/admin -->
            <template v-if="canEdit">
              <!-- Boutons d'édition -->
              <template v-if="isEditing">
                <button class="anamnesis-detail-btn-success" @click="handleSave">
                  <CheckIcon class="h-5 w-5" />
                  Valider
                </button>
                <button class="anamnesis-detail-btn-secondary" @click="handleCancel">
                  <XIcon class="h-5 w-5" />
                  Annuler
                </button>
              </template>
              <button v-else class="anamnesis-detail-btn-primary" @click="handleEdit">
                <EditIcon class="h-5 w-5" />
              </button>

              <!-- Supprimer - seulement pour les propriétaires/admin -->
              <button v-if="canDelete" class="anamnesis-detail-btn-delete" @click="handleDelete">
                <TrashIcon class="h-5 w-5" />
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- Grille principale avec fond blanc et cadres -->
      <div class="anamnesis-detail-grid">
        <!-- Colonne de gauche : Détails -->
        <div class="anamnesis-detail-section bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
            Informations de localisation
          </h3>

          <!-- Adresse -->
          <div class="anamnesis-detail-field">
            <MapPin class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
              <div v-if="isEditing">
                <input v-model="editedLocation.address" class="anamnesis-detail-field-input mb-2"
                  placeholder="Adresse" />
                <div class="flex gap-2 mb-2">
                  <input v-model="editedLocation.post_code" class="anamnesis-detail-field-input w-1/3"
                    placeholder="Code postal" />
                  <input v-model="editedLocation.place" class="anamnesis-detail-field-input w-2/3"
                    placeholder="Ville" />
                </div>
                <input v-model="editedLocation.country" class="anamnesis-detail-field-input" placeholder="Pays" />
              </div>
              <div v-else>
                <div v-if="hasLocationInfo" class="anamnesis-detail-field-text">
                  <p v-if="location?.address">{{ location.address }}</p>
                  <p v-if="location?.post_code || location?.place">{{ location.post_code }} {{ location.place }}</p>
                  <p v-if="location?.country">{{ location.country }}</p>
                </div>
                <p v-else class="anamnesis-detail-field-empty">
                  Aucune adresse renseignée
                </p>
              </div>
            </div>
          </div>

          <!-- Coordonnées GPS -->
          <div class="anamnesis-detail-field">
            <MapPinned class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Coordonnées GPS</label>
              <div v-if="isEditing">
                <div class="flex gap-2 mb-2">
                  <input v-model="editedLocation.latitude" type="number" step="0.000001"
                    class="anamnesis-detail-field-input w-1/2" placeholder="Latitude" />
                  <input v-model="editedLocation.longitude" type="number" step="0.000001"
                    class="anamnesis-detail-field-input w-1/2" placeholder="Longitude" />
                </div>
                <div v-if="showGeocodeButton" class="flex justify-end">
                  <button
                    class="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800"
                    @click="handleGeocode">
                    <RefreshCcwIcon class="h-4 w-4 mr-2" />
                    <span>Rechercher les coordonnées</span>
                  </button>
                </div>
              </div>
              <div v-else>
                <p v-if="location?.latitude && location?.longitude" class="anamnesis-detail-field-text">
                  {{ formatCoordinates(location.latitude, location.longitude) }}
                </p>
                <p v-else class="anamnesis-detail-field-empty">
                  Aucune coordonnée GPS disponible
                </p>
              </div>
            </div>
          </div>

          <!-- Carte -->
          <div v-if="hasCoordinates" class="anamnesis-detail-field">
            <MapPin class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Carte</label>
              <div ref="mapContainer" class="rounded-lg overflow-hidden shadow-lg"
                style="height: 300px; min-height: 300px;">
              </div>
              <p v-if="isEditing" class="text-sm text-gray-600 mt-2 text-center italic">
                Vous pouvez déplacer le marqueur sur la carte pour mettre à jour les coordonnées GPS
              </p>
            </div>
          </div>
        </div>

        <!-- Colonne de droite : Métadonnées -->
        <div class="anamnesis-detail-metadata bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Métadonnées</h3>

          <!-- Métadonnées -->
          <div class="anamnesis-detail-meta-item">
            <Clock class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Créé le</p>
              <p class="anamnesis-detail-meta-text">
                {{ formatDate(location?.created_at) }}
              </p>
            </div>
          </div>

          <div v-if="location?.updated_at && location.updated_at !== location.created_at"
            class="anamnesis-detail-meta-item">
            <Clock class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Modifié le</p>
              <p class="anamnesis-detail-meta-text">
                {{ formatDate(location?.updated_at) }}
              </p>
            </div>
          </div>

          <div v-if="detailedMemos.length" class="anamnesis-detail-meta-item">
            <ClipboardIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Mémos associés</p>
              <p class="anamnesis-detail-meta-text">
                {{ detailedMemos.length }} mémo(s)
              </p>
            </div>
          </div>

          <div v-if="detailedMaterialEntities.length" class="anamnesis-detail-meta-item">
            <ClipboardIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Objets d'étude</p>
              <p class="anamnesis-detail-meta-text">
                {{ detailedMaterialEntities.length }} objet(s) d'étude
              </p>
            </div>
          </div>

          <div class="anamnesis-detail-meta-item">
            <UserIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Propriétaire</p>
              <p class="anamnesis-detail-meta-text">
                {{ ownerName || 'Chargement...' }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Section Mémos avec fond blanc -->
      <div v-if="canViewSubsets" class="anamnesis-detail-memos-section bg-white rounded-lg shadow-sm p-6">
        <h2 class="anamnesis-detail-memos-title">Activités se déroulant à cet emplacement</h2>
        <div v-if="detailedMemos.length">
          <MemosSubset :memos="detailedMemos" :show-delete-button="false" @view="handleMemoView" />
        </div>
        <div v-else class="anamnesis-detail-memos-empty">
          Aucune activité se déroulant à cet emplacement
        </div>
      </div>

      <!-- Section Objets d'étude avec fond blanc -->
      <div v-if="canViewSubsets" class="anamnesis-detail-memos-section bg-white rounded-lg shadow-sm p-6 mt-6">
        <h2 class="anamnesis-detail-memos-title">Objets d'étude originaires de cet emplacement</h2>
        <div v-if="detailedMaterialEntities.length">
          <MaterialEntitySubset :items="detailedMaterialEntities" :show-delete-button="false" :show-edit-button="false"
            :show-duplicate-button="false" :show-actions="false" />
        </div>
        <div v-else class="anamnesis-detail-memos-empty">
          Aucun objet d'étude originaire de cet emplacement
        </div>
      </div>
      
      <!-- Message d'accès restreint -->
      <div v-else class="anamnesis-detail-memos-section bg-white rounded-lg shadow-sm p-6">
        <div class="text-center py-8">
          <EyeOffIcon class="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 class="text-lg font-medium text-gray-900 mb-2">Accès restreint</h3>
          <p class="text-gray-600">
            {{ !authStore.isAuthenticated ? 'Connectez-vous pour voir les contenus associés' : 'Cet asset privé ne vous appartient pas' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Bouton de retour en haut -->
    <ScrollToTop />

    <Notification ref="notification" />
    <ModalAreYouSure v-model="showDeleteModal" title="Supprimer le lieu ?"
      message="Cette action est irréversible. Êtes-vous sûr de vouloir supprimer ce lieu ?" confirm-text="Supprimer"
      cancel-text="Annuler" @confirm="confirmDelete" />
  </div>
</template>

<script setup lang="ts">
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
    Check as CheckIcon,
    Clipboard as ClipboardIcon,
    Clock,
    Edit as EditIcon,
    Eye as EyeIcon,
    EyeOff as EyeOffIcon,
    Lock as LockIcon,
    MapPin,
    MapPinned,
    RefreshCcw as RefreshCcwIcon,
    Trash2 as TrashIcon,
    Unlock as UnlockIcon,
    User as UserIcon,
    X as XIcon
} from 'lucide-vue-next';
import * as Nominatim from 'nominatim-browser';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { deleteLocation, fetchLocationByUuidWithMemos, locations, updateLocation } from '../../api/locations';
import { fetchMaterialEntityWithMemos } from '../../api/materialEntities';
import { fetchMemosUuid } from '../../api/memos';
import type { MaterialEntityWithMemos, Memo } from '../../api/types';
import type { LocationWithMemos } from '../../api/types/';
import { useAssetsCacheStore } from '../../stores/assetsCache';
import { useAuthStore } from '../../stores/auth';
import { useBreadcrumbsStore } from '../../stores/breadcrumbs';
import { useUserNamesCacheStore } from '../../stores/userNamesCache';
import Breadcrumbs from '../Breadcrumbs.vue';
import Notification from '../Notification.vue';
import MaterialEntitySubset from '../subsets/MaterialEntitySubset.vue';
import MemosSubset from '../subsets/MemosSubset.vue';
import ScrollToTop from '../utils/ScrollToTop.vue';
import ModalAreYouSure from '../utils/modal_areyousure.vue';

// Type pour les données d'édition
interface LocationEditData {
  site: string;
  address: string;
  post_code: string;
  place: string;
  latitude?: number;
  longitude?: number;
  country?: string;
  is_public: boolean;
  is_confidential: boolean;
  user_id: string;
}

const route = useRoute();
const router = useRouter();
const breadcrumbsStore = useBreadcrumbsStore();
const authStore = useAuthStore();
const assetsCacheStore = useAssetsCacheStore();
const userNamesCacheStore = useUserNamesCacheStore();
const location = ref<LocationWithMemos | null>(null);
const detailedMemos = ref<Memo[]>([]);
const detailedMaterialEntities = ref<MaterialEntityWithMemos[]>([]);
const isLoading = ref(true);
const mapContainer = ref<HTMLElement | null>(null);
let map: L.Map | null = null;
let marker: L.Marker | null = null;
const isEditing = ref(false);
const isEditingTitle = ref(false);
const editedTitle = ref('');
const editedLocation = ref<LocationEditData>({
  site: '',
  address: '',
  post_code: '',
  place: '',
  latitude: undefined,
  longitude: undefined,
  country: undefined,
  is_public: true,
  is_confidential: false,
  user_id: authStore.user?.uuid || ''
});
const notification = ref<InstanceType<typeof Notification> | null>(null);
const showDeleteModal = ref(false);
const ownerName = ref<string>('');

// Gestion des permissions
const isMyAsset = computed((): boolean => {
  return location.value?.user_id === authStore.user?.uuid;
});

const canEdit = computed((): boolean => {
  return authStore.isAdmin || isMyAsset.value;
});

const canDelete = computed((): boolean => {
  return authStore.isAdmin || isMyAsset.value;
});

const canViewSubsets = computed((): boolean => {
  // Peut voir les subsets si :
  // 1. L'asset est public, OU
  // 2. L'utilisateur est connecté ET est propriétaire de l'asset
  return location.value?.is_public || (authStore.isAuthenticated && isMyAsset.value);
});

// Récupérer l'état depuis history.state
const historyState = window.history.state;
location.value = historyState?.location || null;

const loadDetailedMemos = async (): Promise<void> => {
  if (!location.value?.memos) return;
  try {
    const memoPromises = location.value.memos.map((memo: { uuid: string }) =>
      fetchMemosUuid(memo.uuid)
    );
    const memosData = await Promise.all(memoPromises);
    detailedMemos.value = memosData.map((data: unknown[]) => data[0] as Memo);
  } catch (error) {
    console.error('Erreur lors de la récupération des détails des mémos:', error);
  }
};

const loadDetailedMaterialEntities = async (): Promise<void> => {
  if (!location.value?.material_entities) return;
  try {
    const materialEntityPromises = location.value.material_entities.map((entity: { uuid: string }) =>
      fetchMaterialEntityWithMemos(entity.uuid)
    );
    const entitiesData = await Promise.all(materialEntityPromises);
    detailedMaterialEntities.value = entitiesData;
  } catch (error) {
    console.error('Erreur lors de la récupération des détails des entités matérielles:', error);
  }
};

const loadLocation = async (): Promise<void> => {
  if (location.value) {
    updateBreadcrumb();
    resetEditedData(); // Initialiser editedLocation si pas déjà fait
    await Promise.all([loadDetailedMemos(), loadDetailedMaterialEntities()]);
    await loadOwnerName();
    isLoading.value = false;
    return;
  }

  const uuid = route.params.uuid as string;
  if (!uuid) {
    if (authStore.isAuthenticated) {
      await router.push('/mylocations');
    } else {
      await router.push('/');
    }
    return;
  }

  try {
    let locationData;
    // Si l'utilisateur est connecté, utiliser l'API normale
    if (authStore.isAuthenticated) {
      locationData = await fetchLocationByUuidWithMemos(uuid);
    } else {
      // Sinon, utiliser l'API publique qui permet l'accès par UUID
      locationData = await locations.getPublicByUuidWithMemos(uuid);
    }
    location.value = locationData;
    resetEditedData(); // Initialiser editedLocation avec les nouvelles données
    updateBreadcrumb();
    await Promise.all([loadDetailedMemos(), loadDetailedMaterialEntities()]);
    await loadOwnerName();
  } catch (error) {
    console.error('Erreur lors du chargement du lieu:', error);
    if (authStore.isAuthenticated) {
      await router.push('/mylocations');
    } else {
      await router.push('/');
    }
  } finally {
    isLoading.value = false;
  }
};

const loadOwnerName = async (): Promise<void> => {
  if (!location.value?.user_id) {
    ownerName.value = 'Inconnu';
    return;
  }

  try {
    const name = await userNamesCacheStore.getUserNameAsync(location.value.user_id);
    ownerName.value = name || 'Utilisateur supprimé';
  } catch (error) {
    console.error('Erreur lors du chargement du nom du propriétaire:', error);
    ownerName.value = 'Erreur de chargement';
  }
};

const updateBreadcrumb = (): void => {
  const locationName = location.value?.site || 'Lieu';
  const breadcrumbPath = authStore.isAuthenticated ? '/mylocations' : '/';
  const breadcrumbName = authStore.isAuthenticated ? 'Mes lieux' : 'Accueil';
  breadcrumbsStore.updateBreadcrumbs([
    { name: breadcrumbName, path: breadcrumbPath },
    { name: locationName, path: route.fullPath }
  ]);
};

const resetEditedData = (): void => {
  if (location.value) {
    editedLocation.value = {
      site: location.value.site,
      address: location.value.address || '',
      post_code: location.value.post_code || '',
      place: location.value.place || '',
      latitude: location.value.latitude,
      longitude: location.value.longitude,
      country: location.value.country,
      is_public: location.value.is_public,
      is_confidential: location.value.is_confidential || false,
      user_id: location.value.user_id
    };
    editedTitle.value = location.value.site;
  }
};

const handleEdit = (): void => {
  if (!canEdit.value) return;
  resetEditedData();
  isEditing.value = true;

  // Rendre le marqueur draggable quand on passe en mode édition
  if (marker && marker.dragging) {
    console.log('🗺️ [handleEdit] Making marker draggable');
    marker.dragging.enable();
    marker.on('dragend', handleMarkerDrag);
  }
};

const handleCancel = (): void => {
  isEditing.value = false;
  resetEditedData();
  if (isEditingTitle.value) {
    isEditingTitle.value = false;
  }

  // Désactiver le dragging du marqueur quand on annule l'édition
  if (marker && marker.dragging) {
    console.log('🗺️ [handleCancel] Disabling marker dragging');
    marker.dragging.disable();
    marker.off('dragend', handleMarkerDrag);
  }
};

const handleSave = async (): Promise<void> => {
  if (!location.value || !canEdit.value) return;

  try {
    // S'assurer que si confidentiel, alors privé
    if (editedLocation.value.is_confidential) {
      editedLocation.value.is_public = false;
    }

    const updatedLocation = await updateLocation(location.value.uuid, editedLocation.value);
    location.value = { ...updatedLocation, memos: location.value.memos, material_entities: location.value.material_entities };

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('locations', location.value);

    isEditing.value = false;
    isEditingTitle.value = false;

    // Désactiver le dragging du marqueur après sauvegarde
    if (marker && marker.dragging) {
      console.log('🗺️ [handleSave] Disabling marker dragging after save');
      marker.dragging.disable();
      marker.off('dragend', handleMarkerDrag);
    }

    updateBreadcrumb();
    notification.value?.showNotification('Lieu mis à jour avec succès', 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour du lieu:', error);
    notification.value?.showNotification('Erreur lors de la mise à jour', 'error');
  }
};

const handleDelete = (): void => {
  if (!canDelete.value) return;
  showDeleteModal.value = true;
};

const confirmDelete = async (): Promise<void> => {
  if (!location.value || !canDelete.value) return;

  try {
    await deleteLocation(location.value.uuid);

    // Supprimer du cache
    assetsCacheStore.removeAssetFromCache('locations', location.value.uuid);

    notification.value?.showNotification('Lieu supprimé avec succès', 'success');
    router.push('/mylocations');
  } catch (error) {
    console.error('Erreur lors de la suppression du lieu:', error);
    notification.value?.showNotification('Erreur lors de la suppression', 'error');
  }
  showDeleteModal.value = false;
};

const handlePublicToggle = async (): Promise<void> => {
  if (!location.value || !canEdit.value) return;

  try {
    const updatedLocation = await updateLocation(location.value.uuid, {
      ...location.value,
      is_public: !location.value.is_public
    });
    location.value = { ...updatedLocation, memos: location.value.memos, material_entities: location.value.material_entities };

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('locations', location.value);

    notification.value?.showNotification('Visibilité mise à jour', 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la visibilité:', error);
    notification.value?.showNotification('Erreur lors de la mise à jour de la visibilité', 'error');
  }
};

const handleConfidentialToggle = async (): Promise<void> => {
  if (!location.value || !canEdit.value) return;

  try {
    const newConfidentialStatus = !location.value.is_confidential;
    const updatedLocation = await updateLocation(location.value.uuid, {
      ...location.value,
      is_confidential: newConfidentialStatus,
      // Si on rend confidentiel, forcer à privé
      is_public: newConfidentialStatus ? false : location.value.is_public
    });
    location.value = { ...updatedLocation, memos: location.value.memos, material_entities: location.value.material_entities };

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('locations', location.value);

    notification.value?.showNotification(
      location.value.is_confidential ? 'Location marquée comme confidentielle (privée)' : 'Location marquée comme non confidentielle', 
      'success'
    );
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la confidentialité:', error);
    notification.value?.showNotification('Erreur lors de la mise à jour de la confidentialité', 'error');
  }
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('fr-FR');
};

const formatCoordinates = (lat?: number, lng?: number): string => {
  if (lat && lng) {
    return `${lat.toFixed(3)}°, ${lng.toFixed(3)}°`;
  }
  return 'Non défini';
};

const handleMemoView = (memo: Memo): void => {
  router.push(`/memos/${memo.uuid}`);
};

// Fix Leaflet default marker icons (bundler strips _getIconUrl)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const initMap = (): void => {
  if (!mapContainer.value || !location.value?.latitude || !location.value?.longitude) {
    return;
  }

  // Création de la carte
  map = L.map(mapContainer.value).setView(
    [location.value.latitude, location.value.longitude],
    13
  );

  // Ajout du layer OpenStreetMap
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors'
  }).addTo(map);

  // Force Leaflet à recalculer les dimensions après un petit délai
  setTimeout(() => {
    if (map) {
      map.invalidateSize();
    }
  }, 100);

  // Ajout du marqueur
  marker = L.marker([location.value.latitude, location.value.longitude], {
    draggable: isEditing.value // Le marqueur est déplaçable en mode édition
  })
    .addTo(map)
    .bindPopup(location.value.site || 'Emplacement');

  // Ajout de l'événement de drag si en mode édition
  if (isEditing.value && marker) {
    marker.on('dragend', handleMarkerDrag);
  }
};

onMounted(async () => {
  await loadLocation();

  // Initialisation de la carte après le chargement des données
  nextTick(() => {
    initMap();
  });
});

onUnmounted(() => {
  if (map) {
    map.remove();
  }
});

const startTitleEdit = (): void => {
  if (!canEdit.value) return;
  isEditingTitle.value = true;
  editedTitle.value = location.value?.site || '';
};

const cancelTitleEdit = (): void => {
  isEditingTitle.value = false;
  editedTitle.value = location.value?.site || '';
};

const handleTitleUpdate = async (): Promise<void> => {
  if (!location.value || !canEdit.value || editedTitle.value === location.value.site) {
    isEditingTitle.value = false;
    return;
  }

  try {
    const updatedLocation = await updateLocation(location.value.uuid, {
      ...location.value,
      site: editedTitle.value
    });
    Object.assign(location.value, updatedLocation);

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('locations', location.value);

    notification.value?.showNotification('Titre mis à jour avec succès', 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour du titre:', error);
    notification.value?.showNotification('Erreur lors de la mise à jour du titre', 'error');
  }
  isEditingTitle.value = false;
};

// Modification de handleMarkerDrag
const handleMarkerDrag = (e: L.DragEndEvent): void => {
  if (!editedLocation.value) return;

  const latLng = e.target.getLatLng();
  const newLat = Math.trunc(latLng.lat * 100000) / 100000;
  const newLng = Math.trunc(latLng.lng * 100000) / 100000;

  console.log('🗺️ [handleMarkerDrag] New coordinates:', { lat: newLat, lng: newLng });

  editedLocation.value.latitude = newLat;
  editedLocation.value.longitude = newLng;

  // Rafraîchir la carte
  if (map) {
    map.setView(latLng, map.getZoom());
  }
};

// Modification de handleGeocode
const handleGeocode = async (): Promise<void> => {
  if (!editedLocation.value) return;

  try {
    const searchQuery = [
      editedLocation.value.address,
      editedLocation.value.post_code,
      editedLocation.value.place,
      editedLocation.value.country
    ].filter(Boolean).join(', ');

    const result = await Nominatim.geocode({
      q: searchQuery,
      limit: 1,
      addressdetails: true
    });

    if (result && result.length > 0) {
      editedLocation.value.latitude = Math.trunc(Number(result[0].lat) * 100000) / 100000;
      editedLocation.value.longitude = Math.trunc(Number(result[0].lon) * 100000) / 100000;

      // Si la carte existe déjà, on la supprime pour la réinitialiser
      if (map) {
        map.remove();
        map = null;
        marker = null;
      }

      // On attend le prochain tick pour que le DOM soit mis à jour
      await nextTick();

      // On initialise une nouvelle carte
      if (mapContainer.value) {
        map = L.map(mapContainer.value).setView(
          [editedLocation.value.latitude, editedLocation.value.longitude],
          13
        );

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(map);

        marker = L.marker(
          [editedLocation.value.latitude, editedLocation.value.longitude],
          { draggable: isEditing.value }
        )
          .addTo(map)
          .bindPopup(location.value?.site || 'Emplacement');

        if (isEditing.value) {
          marker.on('dragend', handleMarkerDrag);
        }
      }
    } else {
      notification.value?.showNotification("Aucune coordonnée trouvée pour cette adresse", "warning");
    }
  } catch (error) {
    console.error('Erreur lors de la géolocalisation:', error);
    notification.value?.showNotification("Erreur lors de la recherche des coordonnées", "error");
  }
};

// Computed pour afficher le bouton de géocodage
const showGeocodeButton = computed(() => {
  if (!editedLocation.value) return false;
  return ((editedLocation.value.address && editedLocation.value.post_code) ||
    (editedLocation.value.address && editedLocation.value.place) ||
    (editedLocation.value.post_code && editedLocation.value.place));
});

// Computed pour vérifier si on a des informations de localisation
const hasLocationInfo = computed(() => {
  return !!(location.value?.address || location.value?.post_code || location.value?.place || location.value?.country);
});

// Computed pour vérifier si on a des coordonnées
const hasCoordinates = computed(() => {
  return !!(location.value?.latitude && location.value?.longitude);
});

</script>

<style>
.leaflet-container {
  width: 100%;
  height: 100%;
  z-index: 30 !important;
}

.leaflet-overlay-pane {
  z-index: 31 !important;
}

.leaflet-control {
  z-index: 32 !important;
}

.leaflet-popup-pane {
  z-index: 33 !important;
}
</style>
